import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { caApi } from "../../utils/caApi";

const ProfileContext = createContext(null);
const CACHE_KEY = "dashboardCache";

const readCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
  } catch {
    return null;
  }
};

/**
 * Shared, cached store for the authenticated portal data:
 *   - dashboard (profile card + dashboard stats)
 *   - scoreboard (leaderboard)
 *   - tasks
 * Each endpoint is fetched once and reused across routes, so switching between
 * Dashboard / Leaderboard / Tasks no longer refetches every time. Call the
 * matching `refresh*`/`load*` with force=true after a mutation (e.g. a task
 * submission) to revalidate.
 */
export const ProfileProvider = ({ children }) => {
  const [data, setData] = useState(readCache);
  const [scoreboard, setScoreboard] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const dashInFlight = useRef(false);
  const scoreLoaded = useRef(false);
  const tasksLoaded = useRef(false);

  const refresh = useCallback(async () => {
    if (dashInFlight.current) return;
    if (!localStorage.getItem("token")) return;
    dashInFlight.current = true;
    setLoading(true);
    try {
      const res = await caApi.get("/dashboard");
      setData(res.data);
      setError(null);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(res.data));
      } catch {
        /* storage full / unavailable — in-memory cache still works */
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      dashInFlight.current = false;
    }
  }, []);

  const loadScoreboard = useCallback(async (force = false) => {
    if (scoreLoaded.current && !force) return;
    scoreLoaded.current = true;
    try {
      const res = await caApi.get("/scoreboard");
      setScoreboard(res.data || []);
    } catch (err) {
      scoreLoaded.current = false;
      throw err;
    }
  }, []);

  const loadTasks = useCallback(async (force = false) => {
    if (tasksLoaded.current && !force) return;
    tasksLoaded.current = true;
    try {
      const res = await caApi.get("/task");
      setTasks(res.data || []);
    } catch (err) {
      tasksLoaded.current = false;
      throw err;
    }
  }, []);

  // Auto-refresh so updates (e.g. referral points awarded asynchronously) show
  // up without a manual reload:
  //   - prime on mount (revalidates the cached copy)
  //   - poll the dashboard on an interval while the tab is visible
  //   - revalidate everything immediately when the tab regains focus
  useEffect(() => {
    refresh();

    const POLL_MS = Number(process.env.REACT_APP_DASHBOARD_POLL_MS) || 15000;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, POLL_MS);

    const onFocus = () => {
      if (document.visibilityState !== "visible") return;
      refresh();
      if (scoreLoaded.current) loadScoreboard(true);
      if (tasksLoaded.current) loadTasks(true);
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh, loadScoreboard, loadTasks]);

  return (
    <ProfileContext.Provider
      value={{ data, loading, error, refresh, scoreboard, loadScoreboard, tasks, loadTasks }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () =>
  useContext(ProfileContext) || {
    data: null,
    loading: false,
    error: null,
    refresh: () => {},
    scoreboard: null,
    loadScoreboard: () => {},
    tasks: null,
    loadTasks: () => {},
  };
